import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Sparkle, Check, Plus, Trash, MagnifyingGlass, Wrench, Camera, UserList, X, Receipt, Barcode } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, money, FILE_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from "@/components/ui/drawer";
import { VoiceButton } from "@/components/VoiceButton";
import { ResearchDrawer } from "@/components/ResearchDrawer";
import { ProfitabilityCard } from "@/components/ProfitabilityCard";
import { JobLinks } from "@/components/JobLinks";
import ScheduleForm from "@/components/ScheduleForm";

export default function JobReview() {
  const { id } = useParams();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [researching, setResearching] = useState(false);
  const [generating, setGenerating] = useState("");
  const [customers, setCustomers] = useState([]);
  const [custOpen, setCustOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [researchTask, setResearchTask] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [upcOpen, setUpcOpen] = useState(false);
  const [upcVal, setUpcVal] = useState("");
  const [schedOpen, setSchedOpen] = useState(false);
  const [params] = useSearchParams();
  const fileRef = useRef(null);
  const receiptRef = useRef(null);

  useEffect(() => {
    api.get(`/jobs/${id}`).then((r) => {
      const j = r.data;
      j.materials = (j.materials || []).map((m) => {
        let amt = 0;
        if (!m.customer_supplied) {
          if (m.billing_method === "manual") amt = Number(m.manual_price || 0);
          else {
            const base = m.billing_method === "retail_markup" ? Number(m.retail_price || 0) : Number(m.cost || 0);
            amt = base * Number(m.quantity || 1) * (1 + Number(m.markup_pct || 0) / 100);
          }
        }
        return { ...m, amount: +amt.toFixed(2) };
      });
      setJob(j);
    }).catch(() => toast.error("Job not found"));
    api.get("/customers").then((r) => setCustomers(r.data));
  }, [id]);

  useEffect(() => {
    if (params.get("schedule") === "1" && job) setSchedOpen(true);
  }, [params, job]);

  useEffect(() => {
    if (!job || job.pricing_status !== "pending") return undefined;
    let tries = 0;
    const timer = setInterval(async () => {
      tries += 1;
      try {
        const response = await api.get(`/jobs/${id}`);
        setJob((prev) => mergePriced(prev, response.data));
      } catch { /* keep the draft on screen */ }
      if (tries > 20) clearInterval(timer);
    }, 1500);
    return () => clearInterval(timer);
  }, [id, job?.pricing_status]);

  if (!job) return <div className="p-10 text-center text-zinc-400">Loading…</div>;

  const set = (patch) => setJob({ ...job, ...patch });
  const setTask = (i, patch) => {
    const tasks = [...job.tasks];
    tasks[i] = { ...tasks[i], ...patch };
    setJob({ ...job, tasks });
  };
  const matAmount = (m) => {
    if (m.customer_supplied) return 0;
    const qty = Number(m.quantity || 1);
    const markup = Number(m.markup_pct || 0);
    if (m.billing_method === "manual") return +(Number(m.manual_price || 0)).toFixed(2);
    const base = m.billing_method === "retail_markup" ? Number(m.retail_price || 0) : Number(m.cost || 0);
    return +(base * qty * (1 + markup / 100)).toFixed(2);
  };
  const setMat = (i, patch) => {
    const materials = [...job.materials];
    let m = { ...materials[i], ...patch };
    m.amount = matAmount(m);
    materials[i] = m;
    setJob({ ...job, materials });
  };
  const setExp = (i, patch) => {
    const expenses = [...(job.expenses || [])];
    expenses[i] = { ...expenses[i], ...patch };
    setJob({ ...job, expenses });
  };
  const setBill = (i, patch) => {
    const billables = [...job.billables];
    billables[i] = { ...billables[i], ...patch };
    setJob({ ...job, billables });
  };

  const addTask = () => set({ tasks: [...job.tasks, { id: crypto.randomUUID(), description: "", quantity: 1, hours: 0, recommended_rate: 0, unit_price: 0, amount: 0, accepted: false, rate_basis: "" }] });
  const addMat = () => set({ materials: [...job.materials, { id: crypto.randomUUID(), name: "", brand: "", model_sku: "", category: "", quantity: 1, cost: 0, retail_price: 0, retailer: "", purchase_date: "", markup_pct: 25, billing_method: "actual_markup", manual_price: 0, customer_supplied: false, amount: 0 }] });
  const addExp = () => set({ expenses: [...(job.expenses || []), { id: crypto.randomUUID(), category: "fuel", description: "", cost: 0, billable: false, billed_amount: 0 }] });
  const addBill = () => set({ billables: [...job.billables, { id: crypto.randomUUID(), kind: "service_call", description: "", amount: 0 }] });
  const rm = (key, i) => set({ [key]: job[key].filter((_, idx) => idx !== i) });

  const scanReceipt = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setScanning(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/materials/scan-receipt", fd);
      const items = (r.data.materials || []).map((x) => {
        const m = { id: crypto.randomUUID(), name: x.name || "Material", brand: x.brand || "", model_sku: x.model_sku || "", category: x.category || "", quantity: Number(x.quantity || 1), cost: Number(x.unit_cost || 0), retail_price: 0, retailer: r.data.retailer || "", purchase_date: r.data.purchase_date || "", markup_pct: 25, billing_method: "actual_markup", manual_price: 0, customer_supplied: false, amount: 0 };
        m.amount = matAmount(m); return m;
      });
      setJob((j) => ({ ...j, materials: [...(j.materials || []), ...items] }));
      toast.success(`Added ${items.length} item(s) from receipt`);
    } catch (err) { toast.error(err.response?.data?.detail || "Receipt scan failed"); }
    setScanning(false); e.target.value = "";
  };
  const lookupPrice = async (i) => {
    const m = job.materials[i]; if (!m.name) { toast.error("Enter a product name first"); return; }
    try {
      const r = await api.post("/materials/lookup", { name: m.name, brand: m.brand, model_sku: m.model_sku });
      const patch = {};
      if (r.data.retail_price != null) patch.retail_price = Number(r.data.retail_price);
      if (r.data.retailer) patch.retailer = r.data.retailer;
      if (r.data.category) patch.category = r.data.category;
      if (r.data.brand && !m.brand) patch.brand = r.data.brand;
      if (r.data.model_sku && !m.model_sku) patch.model_sku = r.data.model_sku;
      setMat(i, patch);
      toast.success(r.data.retail_price != null ? `Retail ${money(r.data.retail_price)} @ ${r.data.retailer || "market"}` : "No reliable retail price found");
    } catch (err) { toast.error("Lookup failed"); }
  };
  const addByUpc = async () => {
    if (!upcVal.trim()) return;
    try {
      const r = await api.post("/materials/upc", { upc: upcVal.trim() });
      const x = r.data;
      const m = { id: crypto.randomUUID(), name: x.name || `UPC ${upcVal}`, brand: x.brand || "", model_sku: x.model_sku || "", category: x.category || "", quantity: 1, cost: 0, retail_price: 0, retailer: "", purchase_date: "", markup_pct: 25, billing_method: "actual_markup", manual_price: 0, customer_supplied: false, amount: 0 };
      setJob((j) => ({ ...j, materials: [...(j.materials || []), m] }));
      setUpcOpen(false); setUpcVal("");
      x.found ? toast.success(`Added ${m.name}`) : toast.info("Best guess added — please verify");
    } catch (err) { toast.error("UPC lookup failed"); }
  };

  const save = async () => { await api.put(`/jobs/${id}`, job); };

  const pickCustomer = (c) => { set({ customer_name: c.name, location: job.location || c.address || "" }); setCustOpen(false); };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      let photos = job.photos || [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post(`/jobs/${id}/photos`, fd);
        photos = [...photos, r.data];
      }
      setJob((j) => ({ ...j, photos }));
      toast.success("Photo added");
    } catch (err) { toast.error("Upload failed"); }
    setUploading(false);
    e.target.value = "";
  };

  const removePhoto = async (pid) => {
    await api.delete(`/jobs/${id}/photos/${pid}`);
    setJob((j) => ({ ...j, photos: (j.photos || []).filter((p) => p.id !== pid) }));
  };

  const refine = async (text) => {
    const r = await api.post(`/jobs/${id}/refine`, { transcript: text });
    setJob(r.data.job);
    toast.success(r.data.change_summary || "Updated the job");
  };

  const research = async () => {
    setResearching(true);
    try {
      await save();
      const r = await api.post(`/jobs/${id}/research-rates`);
      setJob(r.data);
      toast.success("Market rates researched");
    } catch (e) { toast.error("Rate research failed"); }
    setResearching(false);
  };

  const accept = (i) => setTask(i, { accepted: true, unit_price: job.tasks[i].recommended_rate, amount: job.tasks[i].recommended_rate });

  const generate = async () => {
    setGenerating("invoice");
    try {
      await save();
      const r = await api.post(`/jobs/${id}/invoice`);
      toast.success("Invoice generated");
      nav(`/invoice/${r.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate invoice"); }
    setGenerating("");
  };

  const makeEstimate = async () => {
    setGenerating("estimate");
    try {
      await save();
      const r = await api.post(`/jobs/${id}/estimate`);
      toast.success("Estimate created");
      nav(`/estimate/${r.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create estimate"); }
    setGenerating("");
  };

  const laborTotal = job.tasks.reduce((s, t) => s + Number(t.amount || 0), 0);
  const matTotal = job.materials.reduce((s, m) => s + Number(m.amount || 0), 0);
  const billTotal = job.billables.reduce((s, b) => s + Number(b.amount || 0), 0);
  const grand = laborTotal + matTotal + billTotal;

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 bg-zinc-50/95 backdrop-blur border-b border-zinc-200 px-4 py-3 flex items-center gap-3">
        <button data-testid="back-btn" onClick={() => nav("/")} className="text-zinc-500"><ArrowLeft size={24} /></button>
        <h1 className="font-heading text-xl font-black tracking-tight">Job Review</h1>
        <button data-testid="job-scan-receipt-btn" onClick={() => nav(`/receipt/scan?job=${id}`)} className="ml-auto flex items-center gap-1.5 text-xs font-bold text-orange-600 border border-orange-200 rounded-full px-3 h-9"><Receipt size={16} weight="bold" /> Scan Receipt</button>
      </header>

      <div className="p-4 space-y-5">
        {/* Voice intake summary */}
        <section className="space-y-3">
          <h2 className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-400">Voice Intake</h2>
          {job.interpretation && (
            <div className="bg-orange-50 border border-orange-100 rounded-xl p-3.5" data-testid="ai-interpretation">
              <p className="text-[10px] tracking-[0.12em] uppercase font-bold text-orange-700 mb-1">AI Interpretation</p>
              <p className="text-sm text-zinc-700 leading-relaxed">{job.interpretation}</p>
            </div>
          )}
          {job.transcript && (
            <details className="bg-white border border-zinc-200 rounded-xl p-3.5" data-testid="transcription">
              <summary className="text-[10px] tracking-[0.12em] uppercase font-bold text-zinc-400 cursor-pointer">Transcription</summary>
              <p className="text-sm text-zinc-600 mt-2 whitespace-pre-wrap">{job.transcript}</p>
            </details>
          )}
          {job.missing_info?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5" data-testid="missing-info">
              <p className="text-[10px] tracking-[0.12em] uppercase font-bold text-amber-700 mb-1">Missing Information</p>
              <ul className="text-sm text-amber-800 list-disc list-inside space-y-0.5">
                {job.missing_info.map((mi, i) => <li key={i} data-testid={`missing-${i}`}>{mi}</li>)}
              </ul>
            </div>
          )}
          <VoiceButton onTranscript={refine} idleLabel="Add More by Voice" testid="add-voice-btn" className="w-full" />
          <p className="text-xs text-zinc-400 leading-relaxed">Tip: tap <b>Add More by Voice</b> to keep describing, or give commands like "add another hour of labor", "change that to two outlets", "remove the service call", "customer supplied the faucet", or "charge $150 for that instead".</p>
        </section>

        {/* Job info */}
        <section className="bg-white border border-zinc-200 rounded-xl p-4 space-y-3">
          <Field label="Customer">
            <div className="flex gap-2">
              <Input data-testid="input-customer" value={job.customer_name} onChange={(e) => set({ customer_name: e.target.value })} className="h-11 flex-1" />
              {customers.length > 0 && (
                <button type="button" data-testid="pick-customer-btn" onClick={() => setCustOpen(true)} className="h-11 px-3 rounded-lg border border-zinc-200 bg-white text-zinc-600 flex items-center gap-1 text-sm font-semibold shrink-0">
                  <UserList size={18} /> Pick
                </button>
              )}
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Location"><Input data-testid="input-location" value={job.location} onChange={(e) => set({ location: e.target.value })} className="h-11" /></Field>
            <Field label="Date"><Input data-testid="input-date" type="date" value={job.date} onChange={(e) => set({ date: e.target.value })} className="h-11" /></Field>
          </div>
          {job.equipment?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {job.equipment.map((eq, i) => (
                <span key={i} className="text-[11px] font-medium bg-zinc-100 text-zinc-600 px-2 py-1 rounded-full flex items-center gap-1"><Wrench size={12} /> {eq}</span>
              ))}
            </div>
          )}
        </section>

        {/* Photos */}
        <section>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple onChange={onFiles} className="hidden" data-testid="photo-input" />
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-400">Job Photos</h2>
            <Button type="button" data-testid="add-photo-btn" onClick={() => fileRef.current?.click()} disabled={uploading} size="sm" variant="outline" className="h-9 gap-1.5">
              <Camera size={16} weight="bold" /> {uploading ? "Uploading…" : "Add Photo"}
            </Button>
          </div>
          {job.photos?.length > 0 ? (
            <div className="grid grid-cols-3 gap-2">
              {job.photos.map((p) => (
                <div key={p.id} className="relative aspect-square rounded-lg overflow-hidden border border-zinc-200" data-testid={`photo-${p.id}`}>
                  <img src={FILE_URL(p.url)} alt="job" className="w-full h-full object-cover" />
                  <button data-testid={`photo-delete-${p.id}`} onClick={() => removePhoto(p.id)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center">
                    <X size={14} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-zinc-400">Snap before/after photos to attach proof of work to this job.</p>
          )}
        </section>

        {job.pricing_status === "pending" && (
          <div data-testid="pricing-pending" className="bg-orange-50 border border-orange-100 rounded-xl px-3 py-2 text-sm text-orange-800 font-semibold">
            Looking up prices… the draft is ready to edit while they fill in.
          </div>
        )}
        {(job.pricing_status === "partial" || job.pricing_status === "failed") && (
          <button data-testid="pricing-retry" onClick={async () => { await api.post(`/jobs/${id}/autoprice`, null, { params: { force: true } }); const r = await api.get(`/jobs/${id}`); setJob((prev) => mergePriced(prev, r.data)); }} className="w-full text-left bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-sm text-amber-800">
            Some prices are still estimates or blank. Tap to try the lookup again.
          </button>
        )}
        {job.won_at && !job.schedule?.start && (
          <div data-testid="schedule-prompt" className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-3">
            <div className="flex-1">
              <p className="text-[10px] uppercase font-bold text-green-700">Job won</p>
              <p className="text-sm font-semibold text-zinc-900">Pick a date for this job.</p>
            </div>
            <Button data-testid="schedule-this-job" onClick={() => setSchedOpen(true)} className="h-10 font-bold">Schedule</Button>
          </div>
        )}

        {/* Tasks */}
        <section>
          <SectionHead title="Tasks & Labor" sub={money(laborTotal)}>
            <Button data-testid="research-rates-btn" onClick={research} disabled={researching} size="sm" className="h-9 gap-1.5">
              {researching ? <MagnifyingGlass size={16} className="animate-pulse" /> : <Sparkle size={16} weight="fill" />}
              {researching ? "Researching…" : "Research Rates"}
            </Button>
          </SectionHead>
          <div className="space-y-3">
            {job.tasks.map((t, i) => (
              <div key={t.id} className="bg-white border border-zinc-200 rounded-xl p-3.5" data-testid={`task-${i}`}>
                <div className="flex items-start gap-2">
                  <Textarea data-testid={`task-desc-${i}`} value={t.description} onChange={(e) => setTask(i, { description: e.target.value })} rows={2} className="flex-1 resize-none text-sm" />
                  <button data-testid={`task-delete-${i}`} onClick={() => rm("tasks", i)} className="text-zinc-300 hover:text-red-500 pt-1"><Trash size={18} /></button>
                </div>
                {(t.trade || t.location_detail || (t.work_types || []).length > 0) && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`task-tags-${i}`}>
                    {t.trade && <span className="text-[10px] font-bold uppercase bg-orange-50 text-orange-700 px-2 py-0.5 rounded">{t.trade}</span>}
                    {t.location_detail && <span className="text-[10px] font-medium bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded">{t.location_detail}</span>}
                    {(t.work_types || []).map((w, wi) => (<span key={wi} className="text-[10px] font-medium bg-blue-50 text-blue-700 px-2 py-0.5 rounded capitalize">{w}</span>))}
                  </div>
                )}
                <div className="grid grid-cols-3 gap-2 mt-2.5">
                  <MiniField label="Qty"><Input data-testid={`task-qty-${i}`} type="number" value={t.quantity} onChange={(e) => setTask(i, { quantity: e.target.value })} className="h-10" /></MiniField>
                  <MiniField label="Hours"><Input data-testid={`task-hours-${i}`} type="number" value={t.hours} onChange={(e) => setTask(i, { hours: e.target.value })} className="h-10" /></MiniField>
                  <MiniField label="Price ($)"><Input data-testid={`task-price-${i}`} type="number" value={t.amount} onChange={(e) => setTask(i, { unit_price: Number(e.target.value), amount: Number(e.target.value), price_source: "manual", price_note: "Edited by you" })} className="h-10 font-semibold" /></MiniField>
                </div>
                <PriceSource source={t.price_source} note={t.price_note} testid={`price-source-${i}`} />
                {t.recommended_rate > 0 && (
                  <div className="mt-2.5 bg-orange-50 rounded-lg px-3 py-2.5" data-testid={`task-research-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-orange-700">
                          Local {money(t.price_low || t.recommended_rate)}–{money(t.price_high || t.recommended_rate)} · Rec. {money(t.recommended_rate)}
                        </p>
                        {t.price_type && <p className="text-[11px] text-orange-600/80 capitalize">{t.price_type}</p>}
                      </div>
                      <button data-testid={`task-accept-${i}`} onClick={() => accept(i)} className={`shrink-0 ml-2 text-xs font-bold px-2.5 py-1.5 rounded-lg flex items-center gap-1 ${t.accepted ? "bg-green-600 text-white" : "bg-white border border-orange-300 text-orange-700"}`}>
                        <Check size={14} weight="bold" /> {t.accepted ? "Applied" : "Accept"}
                      </button>
                    </div>
                    {t.rate_reasoning && <p className="text-[11px] text-orange-700/70 mt-1">{t.rate_reasoning}</p>}
                    {t.research?.sources?.length > 0 && (
                      <button data-testid={`view-research-${i}`} onClick={() => setResearchTask(t)} className="mt-1.5 text-[11px] font-bold text-orange-700 underline underline-offset-2">View Pricing Research</button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          <AddBtn onClick={addTask} testid="add-task">Add task</AddBtn>
        </section>

        {/* Materials */}
        <section>
          <input ref={receiptRef} type="file" accept="image/*" capture="environment" onChange={scanReceipt} className="hidden" data-testid="receipt-input" />
          <SectionHead title="Materials" sub={money(matTotal)} />
          <div className="grid grid-cols-3 gap-2 mb-3">
            <MethodBtn onClick={() => receiptRef.current?.click()} icon={Receipt} label={scanning ? "Scanning…" : "Scan Receipt"} testid="scan-receipt-btn" disabled={scanning} />
            <MethodBtn onClick={() => setUpcOpen(true)} icon={Barcode} label="Add by UPC" testid="add-upc-btn" />
            <MethodBtn onClick={addMat} icon={Plus} label="Manual" testid="add-material" />
          </div>
          <div className="space-y-3">
            {(job.materials || []).map((m, i) => (
              <div key={m.id} className="bg-white border border-zinc-200 rounded-xl p-3.5" data-testid={`material-${i}`}>
                <div className="flex items-center gap-2">
                  <Input data-testid={`mat-name-${i}`} placeholder="Product name" value={m.name} onChange={(e) => setMat(i, { name: e.target.value })} className="flex-1 h-10" />
                  <button data-testid={`mat-lookup-${i}`} onClick={() => lookupPrice(i)} className="h-10 px-2.5 rounded-lg border border-zinc-200 text-zinc-600 shrink-0" title="Look up retail price"><MagnifyingGlass size={16} /></button>
                  <button data-testid={`mat-delete-${i}`} onClick={() => rm("materials", i)} className="text-zinc-300 hover:text-red-500"><Trash size={18} /></button>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Input data-testid={`mat-brand-${i}`} placeholder="Brand" value={m.brand || ""} onChange={(e) => setMat(i, { brand: e.target.value })} className="h-9 text-sm" />
                  <Input data-testid={`mat-sku-${i}`} placeholder="Model / SKU" value={m.model_sku || ""} onChange={(e) => setMat(i, { model_sku: e.target.value })} className="h-9 text-sm" />
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  <select data-testid={`mat-cat-${i}`} value={m.category || ""} onChange={(e) => setMat(i, { category: e.target.value })} className="h-9 text-sm border border-zinc-200 rounded-lg px-2 bg-white capitalize">
                    <option value="">Category…</option>
                    {MAT_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <Input data-testid={`mat-retailer-${i}`} placeholder="Retailer" value={m.retailer || ""} onChange={(e) => setMat(i, { retailer: e.target.value })} className="h-9 text-sm" />
                  <Input data-testid={`mat-date-${i}`} type="date" value={m.purchase_date || ""} onChange={(e) => setMat(i, { purchase_date: e.target.value })} className="h-9 text-sm" />
                </div>
                <div className="grid grid-cols-4 gap-2 mt-2 items-end">
                  <MiniField label="Unit cost"><Input data-testid={`mat-cost-${i}`} type="number" value={m.cost} onChange={(e) => setMat(i, { cost: Number(e.target.value), price_source: "manual", price_note: "Edited by you" })} className="h-9" /></MiniField>
                  <MiniField label="Retail"><Input data-testid={`mat-retail-${i}`} type="number" value={m.retail_price || 0} onChange={(e) => setMat(i, { retail_price: Number(e.target.value) })} className="h-9" /></MiniField>
                  <MiniField label="Qty"><Input data-testid={`mat-qty-${i}`} type="number" value={m.quantity} onChange={(e) => setMat(i, { quantity: Number(e.target.value) })} className="h-9" /></MiniField>
                  <MiniField label="Markup%"><Input data-testid={`mat-markup-${i}`} type="number" value={m.markup_pct} onChange={(e) => setMat(i, { markup_pct: Number(e.target.value) })} className="h-9" /></MiniField>
                </div>
                <MiniField label="Billing method">
                  <select data-testid={`mat-method-${i}`} value={m.billing_method || "actual_markup"} onChange={(e) => setMat(i, { billing_method: e.target.value })} className="h-9 w-full text-sm border border-zinc-200 rounded-lg px-2 bg-white mt-2">
                    <option value="actual_markup">Actual cost + markup</option>
                    <option value="retail_markup">Retail price + markup</option>
                    <option value="manual">Manual customer price</option>
                  </select>
                </MiniField>
                {m.billing_method === "manual" && (
                  <MiniField label="Customer price ($)"><Input data-testid={`mat-manual-${i}`} type="number" value={m.manual_price || 0} onChange={(e) => setMat(i, { manual_price: Number(e.target.value) })} className="h-9 mt-2" /></MiniField>
                )}
                {!m.customer_supplied && (
                  <div className="mt-2 grid grid-cols-4 gap-2 text-center bg-zinc-50 rounded-lg p-2" data-testid={`mat-calc-${i}`}>
                    <Calc label="Cost" val={money((m.cost || 0) * (m.quantity || 1))} />
                    <Calc label="Basis" val={money((m.billing_method === "retail_markup" ? m.retail_price : m.cost) * (m.quantity || 1))} />
                    <Calc label="Customer" val={money(m.amount)} accent />
                    <Calc label="Profit" val={money(m.amount - (m.cost || 0) * (m.quantity || 1))} />
                  </div>
                )}
                <PriceSource source={m.price_source} note={m.price_note} testid={`mat-source-${i}`} />
                <button type="button" data-testid={`mat-supplied-${i}`} onClick={() => setMat(i, { customer_supplied: !m.customer_supplied })} className={`mt-2 text-[11px] font-bold px-2.5 py-1.5 rounded-lg ${m.customer_supplied ? "bg-blue-600 text-white" : "bg-zinc-100 text-zinc-600"}`}>
                  {m.customer_supplied ? "Customer supplied · not billed" : "Mark customer-supplied"}
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Expenses */}
        <section>
          <SectionHead title="Job Expenses" sub={money((job.expenses || []).filter((e) => e.billable).reduce((s, e) => s + Number(e.billed_amount || 0), 0))} />
          <p className="text-[11px] text-zinc-400 mb-2">Track real business costs. Toggle "Bill to customer" to add it to the invoice; either way it counts toward profit.</p>
          <div className="space-y-3">
            {(job.expenses || []).map((x, i) => (
              <div key={x.id} className="bg-white border border-zinc-200 rounded-xl p-3.5" data-testid={`expense-${i}`}>
                <div className="flex items-center gap-2">
                  <select data-testid={`exp-cat-${i}`} value={x.category} onChange={(e) => setExp(i, { category: e.target.value })} className="h-9 text-sm border border-zinc-200 rounded-lg px-2 bg-white capitalize">
                    {EXP_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <Input data-testid={`exp-desc-${i}`} placeholder="Description" value={x.description} onChange={(e) => setExp(i, { description: e.target.value })} className="flex-1 h-9 text-sm" />
                  <button data-testid={`exp-delete-${i}`} onClick={() => rm("expenses", i)} className="text-zinc-300 hover:text-red-500"><Trash size={18} /></button>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2 items-end">
                  <MiniField label="Your cost ($)"><Input data-testid={`exp-cost-${i}`} type="number" value={x.cost} onChange={(e) => setExp(i, { cost: Number(e.target.value) })} className="h-9" /></MiniField>
                  {x.billable
                    ? <MiniField label="Bill customer ($)"><Input data-testid={`exp-billed-${i}`} type="number" value={x.billed_amount} onChange={(e) => setExp(i, { billed_amount: Number(e.target.value) })} className="h-9" /></MiniField>
                    : <div className="text-[11px] text-zinc-400 pb-2">Not billed (cost only)</div>}
                </div>
                <button type="button" data-testid={`exp-billable-${i}`} onClick={() => setExp(i, { billable: !x.billable, billed_amount: !x.billable ? (x.billed_amount || x.cost) : 0 })} className={`mt-2 text-[11px] font-bold px-2.5 py-1.5 rounded-lg ${x.billable ? "bg-orange-600 text-white" : "bg-zinc-100 text-zinc-600"}`}>
                  {x.billable ? "Billing to customer" : "Bill to customer"}
                </button>
              </div>
            ))}
          </div>
          <AddBtn onClick={addExp} testid="add-expense">Add expense (fuel, permit, disposal…)</AddBtn>
        </section>

        {/* Billables */}
        {(job.billables.length > 0) && (
          <section>
            <SectionHead title="Other Billable Items" sub={money(billTotal)} />
            <div className="space-y-3">
              {job.billables.map((b, i) => (
                <div key={b.id} className="bg-white border border-zinc-200 rounded-xl p-3.5" data-testid={`billable-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase bg-zinc-100 text-zinc-600 px-2 py-1 rounded">{b.kind.replace("_", " ")}</span>
                    <button data-testid={`bill-delete-${i}`} onClick={() => rm("billables", i)} className="text-zinc-300 hover:text-red-500 ml-auto"><Trash size={18} /></button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-2.5">
                    <div className="col-span-2"><Input data-testid={`bill-desc-${i}`} value={b.description} onChange={(e) => setBill(i, { description: e.target.value })} className="h-10" placeholder="Description" /></div>
                    <Input data-testid={`bill-amount-${i}`} type="number" value={b.amount} onChange={(e) => setBill(i, { amount: Number(e.target.value) })} className="h-10" placeholder="$" />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
        <AddBtn onClick={addBill} testid="add-billable">Add billable (travel, service call…)</AddBtn>

        <JobLinks jobId={id} />

        <ProfitabilityCard jobId={id} />

        {/* Notes */}
        <section>
          <p className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-400 mb-2">Notes</p>
          <Textarea data-testid="input-notes" value={job.notes} onChange={(e) => set({ notes: e.target.value })} rows={3} />
        </section>
      </div>

      {/* Sticky footer */}
      <div className="sticky bottom-0 bg-white border-t border-zinc-200 p-4 flex items-center gap-3">
        <div>
          <p className="text-[10px] uppercase font-bold text-zinc-400">Job Total</p>
          <p className="font-heading font-black text-2xl text-zinc-900" data-testid="job-total">{money(grand)}</p>
        </div>
        <div className="ml-auto flex flex-col gap-2">
          <Button data-testid="create-estimate-btn" onClick={makeEstimate} disabled={generating} className="h-11 px-4 text-sm font-bold">
            {generating === "estimate" ? "Creating…" : "Create Estimate"}
          </Button>
          <Button data-testid="generate-invoice-btn" onClick={generate} disabled={!!generating} variant="outline" className="h-10 px-4 text-sm font-bold">
            {generating === "invoice" ? "Generating…" : "Generate Invoice"}
          </Button>
        </div>
      </div>

      <ResearchDrawer item={researchTask} onClose={() => setResearchTask(null)} />

      <Drawer open={schedOpen} onOpenChange={setSchedOpen}>
        <DrawerContent className="max-w-md mx-auto">
          <DrawerHeader><DrawerTitle className="font-heading">Schedule this job</DrawerTitle></DrawerHeader>
          <div className="px-4 pb-6">
            <ScheduleForm jobId={id} initial={job.schedule} onDone={(schedule) => { setJob((current) => ({ ...current, schedule })); setSchedOpen(false); }} />
          </div>
        </DrawerContent>
      </Drawer>

      <Drawer open={upcOpen} onOpenChange={setUpcOpen}>
        <DrawerContent className="max-w-md mx-auto">
          <DrawerHeader><DrawerTitle className="font-heading">Add by Barcode / UPC</DrawerTitle></DrawerHeader>
          <div className="px-4">
            <label className="text-[11px] uppercase font-bold text-zinc-400">UPC / barcode number</label>
            <Input data-testid="upc-input" value={upcVal} onChange={(e) => setUpcVal(e.target.value)} placeholder="012345678905" className="h-12 mt-1" />
          </div>
          <DrawerFooter><Button data-testid="upc-add-btn" onClick={addByUpc} disabled={!upcVal.trim()} className="h-12 font-bold">Look up & Add</Button></DrawerFooter>
        </DrawerContent>
      </Drawer>

      <Drawer open={custOpen} onOpenChange={setCustOpen}>
        <DrawerContent className="max-w-md mx-auto">
          <DrawerHeader><DrawerTitle className="font-heading">Pick a Customer</DrawerTitle></DrawerHeader>
          <div className="px-4 pb-6 max-h-[60vh] overflow-y-auto space-y-2">
            {customers.map((c) => (
              <button key={c.id} data-testid={`pick-cust-${c.id}`} onClick={() => pickCustomer(c)} className="w-full text-left bg-white border border-zinc-200 rounded-xl p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center font-heading font-black shrink-0">{c.name?.[0]?.toUpperCase() || "?"}</div>
                <div className="min-w-0"><p className="font-semibold text-zinc-900 truncate">{c.name}</p><p className="text-xs text-zinc-500 truncate">{c.address || c.phone || "—"}</p></div>
              </button>
            ))}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

function mergePriced(local, remote) {
  if (!local) return remote;
  const mergeList = (localList, remoteList) => {
    const remoteById = new Map((remoteList || []).map((item) => [item.id, item]));
    const seen = new Set();
    const merged = (localList || []).map((item) => {
      seen.add(item.id);
      const next = remoteById.get(item.id);
      if (!next || item.price_source === "manual") return item;
      const remoteHasPrice = Number(next.amount || 0) > 0 || Number(next.cost || 0) > 0 || next.price_source;
      if (!remoteHasPrice) return item;
      return {
        ...item,
        amount: next.amount,
        unit_price: next.unit_price ?? item.unit_price,
        cost: next.cost ?? item.cost,
        retail_price: next.retail_price ?? item.retail_price,
        retailer: next.retailer || item.retailer,
        markup_pct: next.markup_pct ?? item.markup_pct,
        price_source: next.price_source || item.price_source,
        price_note: next.price_note || item.price_note,
        recommended_rate: next.recommended_rate ?? item.recommended_rate,
        price_low: next.price_low,
        price_typical: next.price_typical,
        price_high: next.price_high,
        price_type: next.price_type || item.price_type,
        rate_basis: next.rate_basis || item.rate_basis,
        rate_reasoning: next.rate_reasoning || item.rate_reasoning,
        research: next.research || item.research,
        accepted: next.accepted,
      };
    });
    (remoteList || []).forEach((item) => { if (!seen.has(item.id)) merged.push(item); });
    return merged;
  };
  return {
    ...local,
    pricing_status: remote.pricing_status,
    tasks: mergeList(local.tasks, remote.tasks),
    materials: mergeList(local.materials, remote.materials),
    billables: mergeList(local.billables, remote.billables),
  };
}

const PriceSource = ({ source, note, testid }) => {
  if (source !== "live" && source !== "estimated") return null;
  const live = source === "live";
  const detail = (note || "").replace(/^live lookup\s·\s/i, "").replace(/^estimated\s[—-]\s/i, "");
  return (
    <p data-testid={testid} className={`mt-2 text-[10px] font-bold uppercase tracking-wide ${live ? "text-green-700" : "text-amber-700"}`}>
      {live ? "Live lookup" : "Estimated"}{detail ? ` · ${detail}` : ""}
    </p>
  );
};

const Field = ({ label, children }) => (
  <div><label className="text-[11px] tracking-[0.12em] uppercase font-bold text-zinc-400 block mb-1">{label}</label>{children}</div>
);
const MiniField = ({ label, children }) => (
  <div><label className="text-[10px] uppercase font-bold text-zinc-400 block mb-1">{label}</label>{children}</div>
);
const SectionHead = ({ title, sub, children }) => (
  <div className="flex items-center justify-between mb-3">
    <div className="flex items-baseline gap-2">
      <h2 className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-400">{title}</h2>
      <span className="font-heading font-bold text-zinc-900">{sub}</span>
    </div>
    {children}
  </div>
);
const AddBtn = ({ onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick} className="mt-3 w-full border border-dashed border-zinc-300 rounded-xl py-2.5 text-sm font-semibold text-zinc-500 flex items-center justify-center gap-1.5 active:bg-zinc-100">
    <Plus size={16} weight="bold" /> {children}
  </button>
);

const MAT_CATS = ["electrical", "plumbing", "hvac", "hardware", "lumber", "paint", "fasteners", "tools", "appliance", "other"];
const EXP_CATS = ["fuel", "parking", "disposal", "dump", "rental", "delivery", "pickup", "permit", "subcontractor", "other"];

const MethodBtn = ({ onClick, icon: Icon, label, testid, disabled }) => (
  <button type="button" data-testid={testid} onClick={onClick} disabled={disabled} className="h-16 rounded-xl border border-zinc-200 bg-white flex flex-col items-center justify-center gap-1 text-xs font-semibold text-zinc-700 disabled:opacity-50 active:scale-[0.98] transition-transform">
    <Icon size={20} weight="bold" /> {label}
  </button>
);
const Calc = ({ label, val, accent }) => (
  <div><p className="text-[9px] uppercase font-bold text-zinc-400">{label}</p><p className={`text-xs font-bold ${accent ? "text-orange-600" : "text-zinc-800"}`}>{val}</p></div>
);
